import { DateTimeSDK } from '@funkey/shared/utils';

export const NUM_MONTHS = 12;

export type PeriodCutoff = {
    /** 0-indexed month (0 = January, 11 = December). */
    month: number;
    /** 1-indexed day of the month. */
    day: number;
};

export type DateSummary = {
    /** Count of dates falling in `year` (optionally capped by `periodCutoff`). */
    thisYear: number;
    /** Count of dates falling in `year - 1` (capped by the SAME `periodCutoff`). */
    prevYear: number;
    /** Per-month counts for `year`. Always 12 entries, 0-filled. */
    thisYearByMonth: number[];
    /**
     * Number of months in the counted period. `12` when no cutoff is given,
     * `periodCutoff.month + 1` otherwise. Useful for a meaningful "avg per month".
     */
    monthsInPeriod: number;
};

/**
 * Summarises a list of dates for a year-on-year dashboard comparison.
 *
 * When `periodCutoff` is provided (typically "today", passed when the selected
 * year equals the current calendar year), dates in BOTH `year` and `year - 1`
 * are only counted when their month/day is on or before the cutoff. This
 * allows the UI to compare "YTD this year" against "same period last year",
 * rather than a partial year against a full one.
 *
 * All date parts (year/month/day) are resolved in a fixed reporting timezone
 * (Europe/Brussels) so a GraphQL timestamp like `2026-01-01T00:00:00Z` is not
 * miscounted as December 2025 by viewers in negative-offset timezones.
 */
export function summariseDates(
    dates: ReadonlyArray<Date>,
    year: number,
    periodCutoff?: PeriodCutoff,
): DateSummary {
    const thisYearByMonth = new Array<number>(NUM_MONTHS).fill(0);
    let thisYear = 0;
    let prevYear = 0;

    for (const date of dates) {
        const parts = toBrusselsParts(date);
        if (parts.year !== year && parts.year !== year - 1) continue;

        if (periodCutoff && !isOnOrBeforeCutoff(parts, periodCutoff)) continue;

        if (parts.year === year) {
            thisYear += 1;
            thisYearByMonth[parts.month] = (thisYearByMonth[parts.month] ?? 0) + 1;
        } else {
            prevYear += 1;
        }
    }

    return {
        thisYear,
        prevYear,
        thisYearByMonth,
        monthsInPeriod: periodCutoff ? periodCutoff.month + 1 : NUM_MONTHS,
    };
}

type BrusselsParts = {
    /** Full year as seen in Europe/Brussels. */
    year: number;
    /** 0-indexed month (0 = January). */
    month: number;
    /** 1-indexed day of the month. */
    day: number;
};

function toBrusselsParts(date: Date): BrusselsParts {
    const bru = DateTimeSDK.bruDate(date);
    return {
        year: bru.getFullYear(),
        month: bru.getMonth(),
        day: bru.getDate(),
    };
}

function isOnOrBeforeCutoff(parts: BrusselsParts, cutoff: PeriodCutoff): boolean {
    if (parts.month < cutoff.month) return true;
    if (parts.month > cutoff.month) return false;
    return parts.day <= cutoff.day;
}
